declare global {
    interface Window { Viewport: Viewport; }
}

interface ViewportValuesInterface {
    xs?: {up: boolean},
    sm?: {up: boolean, down: boolean},
    md?: {up: boolean, down: boolean},
    lg?: {up: boolean, down: boolean},
    xl?: {up: boolean, down: boolean},
    // xxl?: {up: boolean, down: boolean}
}

interface Breakpoint {
    breakpoint: string,
    size: number
}

/**
 * Get the current viewport
 */
class Viewport {
    public static readonly breakpoints: [
        string,
        string,
        string,
        string,
        string,
        // string,
    ] = [
        'xs',
        'sm',
        'md',
        'lg',
        'xl',
        // 'xxl',
    ];
    public readonly viewportValue: ViewportValuesInterface = {};
    #viewportSizes: [] = [];
    #currentViewport: Breakpoint = {breakpoint: 'xs', size: 0};

    /**
     * Constructor
     */
    constructor() {
        this.#setViewportSizes();
        this.#setCurrentViewport(true);
        this.#setViewports(true);
    }

    /**
     * Getter for the current viewport
     * @return {Breakpoint}
     */
    public get currentViewport(): Breakpoint {
        return this.#currentViewport;
    }

    /**
     * Getter for the viewport
     * @return {ViewportValuesInterface}
     */
    public get viewport(): ViewportValuesInterface {
        return this.viewportValue;
    }

    /**
     * Check if the viewport is lower than a specific breakpoint
     *
     * @param breakpoint
     */
    public mediaBreakpointDown(breakpoint: string): boolean
    {
        const checkMedia = matchMedia('(max-width: ' + (this.#viewportSizes[breakpoint.toLowerCase()].size - 1) + 'px)');

        return checkMedia.matches;
    }

    /**
     * Check if the viewport is higher than a specific breakpoint
     *
     * @param breakpoint
     */
    public mediaBreakpointUp(breakpoint: string): boolean
    {
        const checkMedia = matchMedia('(min-width: ' + this.#viewportSizes[breakpoint.toLowerCase()].size + 'px)');

        return checkMedia.matches;
    }

    /**
     * Check if the viewport is between two breakpoints
     *
     * @param {string} breakpointMin minimum breakpoint
     * @param {string} breakpointMax maximum breakpoint
     */
    public mediaBreakpointBetween(breakpointMin: string, breakpointMax: string): boolean
    {
        const checkMediaMin = matchMedia('(min-width: ' + (this.#viewportSizes[breakpointMin.toLowerCase()].size) + 'px)');
        const checkMediaMax = matchMedia('(max-width: ' + (this.#viewportSizes[breakpointMax.toLowerCase()].size - 1) + 'px)');

        return checkMediaMin.matches && checkMediaMax.matches;
    }

    /**
     * Set all viewport sizes that are linked to the following and previous size
     */
    #setViewportSizes = () => {
        Viewport.breakpoints.forEach((breakpoint, index) => {
            const breakpointSize = getComputedStyle(document.body).getPropertyValue('--breakpoint-' + breakpoint);

            if (breakpointSize) {
                this.#viewportSizes[breakpoint] = {
                    size: parseInt(breakpointSize.replace('px', '')),
                    after: index >= Viewport.breakpoints.length ? null : Viewport.breakpoints[index + 1],
                    before: index === 0 ? null : Viewport.breakpoints[index - 1]
                };
            }
        });
    }

    /**
     * Set all values for the viewports
     *
     * @param {boolean} initEventListener
     */
    #setViewports(initEventListener: boolean = false) {
        if (!this.#viewportSizes) {
            this.#setViewportSizes();
        }
        Viewport.breakpoints.forEach((breakpoint, index) => {
            this.viewport[breakpoint] = {
                up: true
            };

            // Skip the first size because it is the smallest one
            if (index > 0) {
                this.viewport[breakpoint].up = true;

                if ('matchMedia' in window) {
                    const breakpointSize = this.#viewportSizes[breakpoint].size,
                        mqUp = matchMedia(`(min-width: ${breakpointSize}px`);

                    this.viewport[breakpoint].up = mqUp.matches;

                    if (initEventListener) {
                        mqUp.addEventListener('change', () => {
                                this.viewport[breakpoint].up = mqUp.matches;
                            },
                            {passive: true});
                    }
                }

                // skip the last breakpoint
                if (index + 1 < Viewport.breakpoints.length) {
                    this.viewport[breakpoint].down = true;

                    if ('matchMedia' in window) {
                        const nextBreakpoint = Viewport.breakpoints[index + 1],
                            maxWidthSize = this.#viewportSizes[nextBreakpoint].size - 1,
                            mqDown = matchMedia(`(max-width: ${maxWidthSize}px)`);

                        this.viewport[breakpoint].down = mqDown.matches;

                        if (initEventListener) {
                            mqDown.addEventListener('change', (mq) => {
                                    this.viewport[breakpoint].down = mq.matches;
                                },
                                {passive: true});
                        }
                    }
                }
            }
        });
    }

    /**
     * Set the current viewport
     * @param {boolean} initEventListener
     */
    #setCurrentViewport(initEventListener: boolean = false) {
        if (!this.#viewportSizes) {
            this.#setViewportSizes();
        }
        let currentViewport: string;

        Viewport.breakpoints.forEach((breakpoint) => {
            const checkMedia = matchMedia('(min-width: ' + this.#viewportSizes[breakpoint].size + 'px)');

            if (checkMedia.matches) {
                currentViewport = breakpoint;
            }

            if (initEventListener) {
                checkMedia.addEventListener('change', () => {
                    this.#setCurrentViewport();
                }, {passive: true});
            }
        });

        this.#currentViewport = {
            breakpoint: currentViewport,
            size: this.#viewportSizes[currentViewport].size
        };
    }
}
export default Viewport;
